/**
 * @nostr-post/web - Styles for <nostr-post-view>
 */

import { css } from 'lit';

export const viewStyle = css`
  .view {
    padding: 1rem;
    border: 1px solid var(--nl-border, #e5e7eb);
    border-radius: 8px;
    background: var(--nl-card-bg, #f9fafb);
  }

  :host-context(.dark) .view {
    background: #374151;
    border-color: #4b5563;
  }

  .view-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    font-size: 12px;
  }

  .view-action {
    margin-left: auto;
  }

  .view-avatar {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    object-fit: cover;
    border: 1px solid var(--nl-border, #d1d5db);
    flex-shrink: 0;
  }

  .view-author {
    font-weight: 600;
    color: var(--nl-text, #111827);
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :host-context(.dark) .view-author {
    color: #f3f4f6;
  }

  .view-pubkey {
    font-family: monospace;
    color: var(--nl-text-secondary, #6b7280);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .view-timestamp {
    color: var(--nl-text-secondary, #6b7280);
    margin-left: auto;
  }

  .view-content {
    white-space: normal;
    word-break: break-word;
    line-height: 1.6;
    color: var(--nl-text, #1f2937);
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .view-content > * {
    margin: 0;
  }

  .view-content np-hashtag-view,
  .view-content np-media-view,
  .linked-field np-hashtag-view,
  .linked-field np-media-view {
    align-self: start;
  }

  :host-context(.dark) .view-content {
    color: #f3f4f6;
  }

  .view-manifest-note {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    color: var(--nl-text-secondary, #6b7280);
  }

  .note-link,
  .note-hashtag {
    color: var(--nl-primary, #6366f1);
    text-decoration: none;
    word-break: break-all;
  }

  .note-link:hover {
    text-decoration: underline;
  }

  .note-media {
    display: block;
    width: fit-content;
    max-width: 100%;
    margin: 0.5rem 0;
  }

  .note-media img,
  video.note-media {
    display: block;
    max-width: 100%;
    max-height: 28rem;
    border-radius: 8px;
  }

  .note-code {
    background: #f3f4f6;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
  }

  .note-heading {
    margin: 1rem 0 0.5rem 0;
    font-size: 1.1rem;
  }

  .view-tags {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--nl-border, #e5e7eb);
  }

  :host-context(.dark) .view-tags {
    border-color: #4b5563;
  }

  .tag {
    display: inline-block;
    padding: 2px 8px;
    margin: 0.25rem 0.25rem 0.25rem 0;
    background: var(--nl-tag-bg, #e5e7eb);
    border-radius: 4px;
    font-size: 12px;
    color: var(--nl-text-secondary, #6b7280);
  }

  :host-context(.dark) .tag {
    background: #4b5563;
    color: #d1d5db;
  }

  .tag-name {
    font-weight: 600;
    margin-right: 0.25rem;
  }

  .view-kind {
    display: inline-block;
    padding: 2px 8px;
    background: var(--nl-primary, #6366f1);
    color: white;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }

  .view-id {
    margin-top: 0.35rem;
    font-size: 11px;
    font-family: monospace;
    color: var(--nl-text-secondary, #9ca3af);
    word-break: break-all;
  }

  .linked-data {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--nl-border, #e5e7eb);
  }

  :host-context(.dark) .linked-data {
    border-color: #4b5563;
  }

  .linked-data-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--nl-text-secondary, #9ca3af);
    margin-bottom: 0.5rem;
  }

  .linked-field {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
  }

  .linked-field-label[title] {
    cursor: help;
    text-decoration: underline dotted;
  }

  .linked-field-label {
    font-weight: 500;
    color: var(--nl-text-secondary, #6b7280);
    min-width: 100px;
  }

  :host-context(.dark) .linked-field-label {
    color: #9ca3af;
  }

  .view-edit-button {
    margin-top: 0.5rem;
    padding: 0.25rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    background: transparent;
    color: var(--nl-accent, #8b5cf6);
    border: 1px solid var(--nl-accent, #8b5cf6);
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .view-edit-button:hover {
    background: var(--nl-accent, #8b5cf6);
    color: white;
  }

  .view-updates {
    margin-top: 0.5rem;
    font-size: 0.8125rem;
    color: var(--nl-text-secondary, #6b7280);
  }

  .view-updates-summary {
    cursor: pointer;
    user-select: none;
    color: var(--nl-text-secondary, #6b7280);
  }

  .view-updates-list {
    margin: 0.25rem 0 0 1rem;
    padding: 0;
    list-style: disc;
  }

  .view-updates-list li {
    margin-bottom: 0.125rem;
  }

  .view-suggestion-author {
    font-family: monospace;
  }
  .view-suggestion-line {
    margin-right: 0.5rem;
  }
  .view-suggestion-toggle {
    margin-left: 0.25rem;
    padding: 0.125rem 0.5rem;
    font-size: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
  .view-suggestion-toggle[aria-pressed='true'] {
    border-color: #6366f1;
    color: #4f46e5;
  }
  :host-context(.dark) .view-suggestion-toggle {
    border-color: #4b5563;
  }
  :host-context(.dark) .view-suggestion-toggle[aria-pressed='true'] {
    border-color: #a5b4fc;
    color: #a5b4fc;
  }

  .view-inline-composer {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--nl-border, #e5e7eb);
  }
  .view-edit-hint {
    margin: 0 0 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }
  :host-context(.dark) .view-edit-hint {
    color: #9ca3af;
  }
  details.view-raw {
    margin-top: 0.75rem;
    font-size: 0.75rem;
  }
  details.view-raw summary {
    cursor: pointer;
    color: var(--nl-text-secondary, #6b7280);
    font-weight: 500;
  }
  details.view-raw dl {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.15rem 0.75rem;
    margin: 0.5rem 0;
    word-break: break-all;
  }
  details.view-raw dd { margin: 0; }
  details.view-raw h4 { margin: 0.6rem 0 0.25rem; font-size: 0.75rem; }
  details.view-raw pre {
    margin: 0;
    padding: 0.5rem;
    max-height: 16rem;
    overflow: auto;
    white-space: pre-wrap;
    border-radius: 0.375rem;
    background: var(--nl-card-bg, #f9fafb);
  }

  /* A referenced entity in a role (e.g. "Tasted at"), its wiki page opens on demand */
  .post-role {
    margin-top: 0.5rem;
    font-size: 0.875rem;
  }
  .post-role-label {
    color: var(--nl-muted, #4b5563);
    margin-bottom: 0.25rem;
  }
  .post-role summary {
    cursor: pointer;
    color: var(--nl-muted, #4b5563);
  }

  /* Tag fields with a built-in input: a title tag as heading, others as label: value */
  .tag-title {
    font-size: 1.0625rem;
    font-weight: 600;
    margin: 0.25rem 0;
  }
  .tag-row {
    font-size: 0.875rem;
    margin: 0.25rem 0;
  }
  .tag-label {
    color: var(--nl-muted, #4b5563);
  }
  :host-context(.dark) .tag-label {
    color: #9ca3af;
  }
`;
